import { Component, Inject, NgModule } from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule, AsyncPipe } from '@angular/common';
import { combineLatest, of, Subject, map, startWith, switchMap, tap } from 'rxjs';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA, MatDialogClose } from '@angular/material/dialog';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatIconModule, MatIcon } from '@angular/material/icon';
import { UntypedFormControl, UntypedFormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule, MatCheckbox } from '@angular/material/checkbox';
import { DEFAULT_OPTION } from '@uw/core/constants';
import { CatalogMgmtService } from '@uw/core/services/catalog-mgmt.service';
import { CreateUpdateSUWsRequest, CreateUpdateSUWsResponse } from '@uw/core/api/models';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '@uw/core/services/auth.service';
import { provinces } from '@uw/core/services/look-up.service';
import { CatalogManagementStore } from '@uw/modules/uwsetup/pages/catalog-management-page/catalog-management.store';



@Component({
    selector: 'uw-add-a-new-servicing-united-way-dialog',
    templateUrl: './add-a-new-servicing-united-way-dialog.component.html',
    styleUrls: ['./add-a-new-servicing-united-way-dialog.component.scss'],
    imports: [A11yModule, MatIconButton, MatDialogClose, MatIcon, ReactiveFormsModule, MatCheckbox, AsyncPipe, TranslateModule]
})
export class AddANewServicingUnitedWayDialogComponent {

  get user() {
    return this._authService.getUser();
  }

  isEdit: boolean = false;

  readonly DEFAULT_OPTION = DEFAULT_OPTION;

  readonly saCatalogs$ = this._catalogMgmtService.getCatalogsByCatalogType("SA");

  readonly cfCatalogs$ = this._catalogMgmtService.getCatalogsByCatalogType("CF")

  readonly cat2Catalogs$ = this._catalogMgmtService.getCatalogsByCatalogType("CAT2");

  private readonly _saveSubject: Subject<CreateUpdateSUWsRequest> = new Subject();

  readonly form: UntypedFormGroup = new UntypedFormGroup({
    name: new UntypedFormControl(null, [Validators.required]),
    externalCatalogSetId: new UntypedFormControl(null, [Validators.required]),
    isEnabled: new UntypedFormControl(null, []),
    cfId: new UntypedFormControl(null),
    saId: new UntypedFormControl(null),
    cat2Id: new UntypedFormControl(null),
    unitedWayId: new UntypedFormControl(this.user?.unitedWayId, [Validators.required]),
    userId: new UntypedFormControl(this.user?.userId, [Validators.required]),
   // regionUnitedWayId: new UntypedFormControl(this.user?.unitedWayId, [Validators.required]),
    province: new UntypedFormControl("ON", [Validators.required]),
    campaignId: new UntypedFormControl(null, []),
    catalogSetId: new UntypedFormControl(null, [])
  });

  provinces: typeof provinces = provinces;

  get hasInvalidDefaults() {
    return this.form.value.cfId == DEFAULT_OPTION || this.form.value.saId == DEFAULT_OPTION || this.form.value.cat2Id == DEFAULT_OPTION
  }

  unitedWays$ = this._catalogMgmtService.getUnitedWays();

  readonly vm$ = combineLatest([
    this.saCatalogs$,
    this.cfCatalogs$,
    this.cat2Catalogs$,
    this._saveSubject.pipe(
      switchMap(request => {
        const obs$ = request.catalogSetId ? this._catalogMgmtService.updateSuw(request) : this._catalogMgmtService.createSuw(request);
        return obs$.pipe(
          tap(suws => this._dialogRef.close(suws))
        )
      }),
      startWith(null)
    )
  ]).pipe(
    map(([saCatalogs, cfCatalogs, cat2Catalogs]) => ({ saCatalogs, cfCatalogs, cat2Catalogs }))
  );

  constructor(
    @Inject(MAT_DIALOG_DATA) data: { suw: CreateUpdateSUWsResponse, activeCampaignId: number },
    private readonly _catalogMgmtService: CatalogMgmtService,
    private readonly _dialogRef: MatDialogRef<AddANewServicingUnitedWayDialogComponent>,
    private readonly _authService: AuthService
  ) {
    if (data?.suw) {
      this.form.patchValue({
        ...data.suw,
        ...{
          unitedWayId: this.user?.unitedWayId,
          userId: this.user?.userId
        }
      }, { emitEvent: false });
      this.isEdit = true;
    }

    if (data?.activeCampaignId) {
      this.form.patchValue({
        campaignId: data.activeCampaignId
      });
      this.form.markAsDirty();
    }
  }

  handleSave(request: CreateUpdateSUWsRequest) {
    this._saveSubject.next(request);
  }
}

@NgModule({
    exports: [
        AddANewServicingUnitedWayDialogComponent
    ],
    imports: [
        A11yModule,
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        ReactiveFormsModule,
        MatCheckboxModule,
        TranslateModule,
        AddANewServicingUnitedWayDialogComponent
    ]
})
export class AddANewServicingUnitedWayDialogModule { }
